import { last, sample } from "lodash";
import { IoSparklesOutline } from "react-icons/io5";
import Typewriter from "typewriter-effect";
import GradientText from "../../../../../Logo";
import { AssetReport } from "../../../../../../@types/Asset";
import { ReactElement, useEffect, useState } from "react";
import useWindowDimensions from "../../../../../../hooks/useWindowDimension";
import { Spinner } from "react-bootstrap";
import InteractResumeButton from "./InteractButton";
import { useTour } from "@reactour/tour";

type Props = {
  report: AssetReport;
  assetId: string;
  switchElement: ReactElement;
};
export default function ResumeContent({
  report,
  assetId,
  switchElement,
}: Props) {
  const [showMore, setShowMore] = useState<boolean>(false);

  const { width } = useWindowDimensions();
  const { isOpen } = useTour();

  useEffect(() => {
    setShowMore(false);
  }, [assetId]);

  if (!report) {
    return <EmptyLoadingState switchElement={switchElement} />;
  }

  return (
    <div className="card card-dashboard-thirteen" id="analysis-step-2">
      <h3>
        <GradientText text={"Lucy AI"} />
        <IoSparklesOutline style={{ color: "var(--primary-blue)" }} size={25} />
      </h3>
      <p className="az-content-text">
        <GradientText
          text={"Observe a opnião resumida da nossa I.A com Natural Language."}
        />
      </p>

      {switchElement}

      <div className="animated fadeInDown">
        <div className={isOpen ? "tx-18 fixed-height" : "tx-18"}>
          <Typewriter
            onInit={(typewriter) => {
              const trimmedResume = report.ai_model_resume.trim();
              const splittedResume = trimmedResume.split(". ");

              splittedResume.forEach((sentence: string, i: number) => {
                const lastSentence = last(splittedResume);
                const isEven = i % 2 == 0;
                const isLastSentence = sentence === lastSentence;

                typewriter.typeString(sentence);

                if (isLastSentence) {
                  typewriter.callFunction(() => {
                    setShowMore(true);
                  });
                  return;
                }

                if (isEven) {
                  typewriter.typeString(". <br/> <br/>");
                } else {
                  typewriter.typeString(". ");
                }
              });

              typewriter.start();
            }}
            options={{
              delay: 20,
            }}
          />
        </div>
      </div>

      {showMore && (
        <InteractResumeButton assetId={assetId} reportId={report.id} />
      )}

      {showMore && width > 768 && (
        <div className="wrapper mg-t-10 animated fadeInDown">
          <div className="row justify-content-center tx-15">
            <div className="col-4 text-center tx-semibold">
              <GradientText text={"#" + report.tag1} />
            </div>

            <div className="col-4 text-center tx-semibold">
              <GradientText text={"#" + report.tag2} />
            </div>

            <div className="col-4 text-center tx-semibold">
              <GradientText text={"#" + report.tag3} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyLoadingState({ switchElement }: { switchElement: ReactElement }) {
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const firstLinesTexts = [
    "Hmm, já tem um tempinho que ninguém avalia este ativo, por isso estou atualizando meus dados para te fornecer uma análise mais recente.",
    "Poxa, meus dados sobre esse ativo estão desatualizados, estou atualizando agora mesmo.",
    "Faz tempo que ninguém acessa esse ativo, me deixa tirar a poeira e atualizar as coisas por aqui.",
    "Pausei minha série só para analisar este ativo para você, espero que goste. Estou atualizando meus dados.",
    "Estou atualizando meus dados sobre esse ativo e já irei te fornecer uma análise detalhada.",
    "Faz um período de tempo desde que alguém explorou este ativo, por isso, estou renovando minhas informações para te proporcionar uma avaliação mais moderna.",
    "Vejo que este ativo tem estado fora do nosso radar, vou sacudir a poeira e trazer as novidades mais quentinhas pra você.",
    "Parece que este ativo tirou uma soneca longa, vou dar um tapa nele e trazer uma avaliação fresquinha pra você.",
    "É chorão, ando esperando pelos meus dias de glória. Enquanto isso, vou atualizar meus dados sobre este ativo.",
    "Preciso atualizar minha base de dados sobre esse ativo, vai ser mais rápido que o Neymar caindo em campo.",
  ];
  const secondLinesTexts = [
    "Você pode aguardar aqui ou ir vendo as impressões por enquanto. Você também pode voltar mais tarde caso prefira.",
    "Você pode aguardar aqui ou clicar no botão localizado ao lado do nome 'Impressões' para ver minhas impressões atuais.",
    "Enquanto isso, você pode visualizar as impressões. Se quiser, você também pode voltar mais tarde que tudo vai estar pronto.",
    "Não vai demorar tanto, você pode ir vendo dados brutos e navegando enquanto isso.",
    "Vai ser rápido, você pode voltar daqui a pouco ou ver as impressões por enquanto.",
    "Que tal ir vendo as impressões por enquanto? Lá tem dados super interessantes!",
  ];

  const firstLine = sample(firstLinesTexts);
  const secondLine = sample(secondLinesTexts);

  return (
    <div className="card card-dashboard-thirteen" id="analysis-step-2">
      <h3>
        <GradientText text={"Lucy AI"} />
        <IoSparklesOutline style={{ color: "var(--primary-blue)" }} size={25} />
      </h3>
      <p className="az-content-text">
        <GradientText
          text={"Observe a opnião resumida da nossa I.A com Natural Language."}
        />
      </p>

      {switchElement}

      <div className="animated fadeInDown">
        <div className="tx-18 mg-b-20">
          <Typewriter
            onInit={(typewriter) => {
              typewriter.typeString(String(firstLine));
              typewriter.typeString("<br/> <br/>");

              typewriter.typeString(String(secondLine));

              typewriter.callFunction(() => {
                setTimeout(() => {
                  setShowSpinner(true);
                }, 1000);
              });

              typewriter.start();
            }}
            options={{
              delay: 20,
            }}
          />
        </div>

        {showSpinner && (
          <i className="animated fadeInDown">
            Lucy está avaliando este ativo, isso pode levar alguns segundos...
            <Spinner className="mg-l-10" animation="border" size="sm" />
          </i>
        )}
      </div>
    </div>
  );
}
